import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/** Guards against overlapping swap requests mutating the cart concurrently. */
let swapping = false;

/**
 * Asks Shopify's cart API to also return freshly rendered HTML for every cart
 * section on the page, so the cart list can morph straight from the response.
 * @returns {{sections?: string, sections_url?: string}}
 */
function sectionsParams() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  if (ids.size === 0) return {};
  return { sections: Array.from(ids).join(','), sections_url: window.location.pathname };
}

/**
 * Lets a customer change which variant (e.g. colour/size) a cart line is
 * for, directly from the cart drawer/page, without leaving the cart. Since
 * Shopify's cart API can't change a line's variant in place, this removes
 * the existing line and adds the newly selected variant with the same
 * quantity and line item properties (including e.g. the free-gift-with-
 * purchase "_free_gift" tag, so swapping a gift's colour keeps it free).
 *
 * The line's current quantity/properties/key are always read fresh from a
 * live cart.js fetch immediately before acting, rather than from data
 * attributes baked into the page at render time - other cart activity
 * (e.g. the free-gift-with-purchase auto-add/remove system) can re-render
 * this part of the cart in the background, and a stale properties value
 * caused the new line to be added without its "_free_gift" tag, which in
 * turn made that background system think the gift was missing and add
 * another one back - the actual cause of gifts appearing to duplicate.
 *
 * @extends {Component}
 */
class CartItemVariantPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
  }

  /** @returns {Array<{id: number, available: boolean, option1?: string, option2?: string, option3?: string}>} */
  get #variants() {
    const dataEl = this.querySelector('script[data-variants]');
    if (!dataEl?.textContent) return [];

    try {
      return JSON.parse(dataEl.textContent);
    } catch {
      return [];
    }
  }

  #handleChange = () => {
    const selects = /** @type {HTMLSelectElement[]} */ (
      Array.from(this.querySelectorAll('select[data-option-position]'))
    );

    const selectedValues = selects
      .slice()
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((select) => select.value);

    const match = this.#variants.find((variant) =>
      selectedValues.every((value, index) => variant[`option${index + 1}`] === value)
    );

    this.#clearError();

    if (!match) {
      this.#showError("That combination isn't available.");
      return;
    }

    if (!match.available) {
      this.#showError('That variant is currently sold out.');
      return;
    }

    this.#swapTo(match.id);
  };

  /** @param {string} text */
  #showError(text) {
    const errorEl = this.querySelector('.cart-items__variant-error');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = text;
    errorEl.hidden = false;
  }

  #clearError() {
    const errorEl = this.querySelector('.cart-items__variant-error');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.hidden = true;
    errorEl.textContent = '';
  }

  /** @param {number} newVariantId */
  #swapTo(newVariantId) {
    if (swapping) return;
    swapping = true;
    this.classList.add('cart-items__variants--updating');

    const currentVariantId = this.dataset.currentVariantId;

    fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        const currentLine = cart.items.find((item) => String(item.variant_id) === String(currentVariantId));
        if (!currentLine) throw new Error('Could not find the current line to swap.');

        const properties =
          currentLine.properties && typeof currentLine.properties === 'object' && !Array.isArray(currentLine.properties)
            ? currentLine.properties
            : {};

        // Add the new variant first, and only remove the old line once that
        // succeeds - if the add fails for any reason, the original line is
        // still untouched instead of the item just disappearing.
        return fetch(
          Theme.routes.cart_add_url,
          fetchConfig('json', {
            body: JSON.stringify({
              items: [
                {
                  id: newVariantId,
                  quantity: currentLine.quantity,
                  properties,
                },
              ],
            }),
          })
        )
          .then((response) => response.json())
          .then((result) => {
            if (result.status) throw new Error(result.description || 'Failed to switch variant');

            // Ask for freshly rendered cart section HTML in the same request,
            // so the cart list morphs straight from it rather than making a
            // second, slow full-section fetch afterwards.
            return fetch(
              Theme.routes.cart_change_url,
              fetchConfig('json', {
                body: JSON.stringify({ id: currentLine.key, quantity: 0, ...sectionsParams() }),
              })
            );
          })
          .then((response) => response.json())
          .then((result) => {
            if (result.status) {
              console.error('[cart-item-variant-picker] failed to remove previous line', result);
              throw new Error(result.description || result.message || 'Failed to remove previous variant');
            }
            return result;
          });
      })
      .then((cart) => {
        document.dispatchEvent(
          new CartUpdateEvent(cart, this.id || 'cart-item-variant-picker', {
            source: 'cart-item-variant-picker',
            // The header cart bubble reads this - without it the count reset to 0.
            itemCount: cart.item_count,
            sections: cart.sections,
          })
        );
      })
      .catch((error) => {
        console.error('[cart-item-variant-picker]', error);
        this.#showError('Something went wrong - please try again.');
      })
      .finally(() => {
        swapping = false;
        this.classList.remove('cart-items__variants--updating');
      });
  }
}

if (!customElements.get('cart-item-variant-picker')) {
  customElements.define('cart-item-variant-picker', CartItemVariantPicker);
}
